import { isRecipeMeal } from '../../../redux/mealEntry';
import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import { displayLanguageOf } from '../../../utils/recipeLanguage';

/**
 * Build a minimal RecipeIndexEntry from a meal's denormalized recipe.
 * A planned meal whose recipeId is missing from the recipe index would
 * otherwise render as an EMPTY slot — the meal exists (it is counted in the
 * week summary) but its card vanishes and the slot invites overwriting it.
 */
export function indexEntryFromMeal(meal: PlannedMeal): RecipeIndexEntry | null {
  const recipe = meal.recipe;
  if (!recipe || !recipe.title) return null;
  return {
    id: meal.recipeId,
    title: recipe.title,
    image: recipe.image,
    servings: recipe.servings ?? meal.servings ?? 4,
    servingsDisplay: recipe.servingsDisplay ?? String(recipe.servings ?? meal.servings ?? 4),
    readyInMinutes: recipe.readyInMinutes ?? 0,
    dishTypes: recipe.dishTypes ?? [],
    cuisines: recipe.cuisines ?? [],
    diets: recipe.diets ?? [],
    ingredients: [],
    searchText: recipe.title.toLowerCase(),
    sourceUrl: recipe.sourceUrl,
    // What the planner row reads to print a title in the reader's language
    // (#737): without these the fallback prints the source title.
    translatedTitles: recipe.translatedTitles,
    originalLanguage: recipe.originalLanguage === 'unknown' ? undefined : recipe.originalLanguage,
    languages: (recipe as { languages?: ('en' | 'fr')[] }).languages,
  };
}

/**
 * Backfill an id → RecipeIndexEntry map with fallback entries for the week's
 * recipe meals the index cannot resolve. Mutates and returns `map`.
 */
export function backfillIndexFromMeals(
  map: Record<string, RecipeIndexEntry>,
  meals: PlannedMeal[],
): Record<string, RecipeIndexEntry> {
  meals.forEach((meal) => {
    if (!isRecipeMeal(meal) || map[meal.recipeId]) return;
    const fallback = indexEntryFromMeal(meal);
    if (fallback) map[meal.recipeId] = fallback;
  });
  return map;
}

/**
 * The index a planner card reads, with every title in the cook's language.
 *
 * Every card — the hero, a day's row, a proposal, the toasts — reads its title
 * from this map, and the map is the recipe index: titles in the language the
 * recipe was written in. The meal's own copy, which the translation hook
 * rewrites, only ever showed when the index lacked the recipe. So a French cook
 * saw "Fish Tacos" and "Vegan Tantanmen With Pan-Fried Tofu" on the plan while
 * the catalogue held "Tacos de poisson" and a French title for the other (#736).
 *
 * The catalogue's own translated title wins: the server writes it, and a guest
 * has it without being able to reach the translate endpoint. Then a title the
 * meal already carries in that language. Then the recipe's own.
 */
export function localizeIndex(
  map: Record<string, RecipeIndexEntry>,
  meals: PlannedMeal[],
  language: 'en' | 'fr',
): Record<string, RecipeIndexEntry> {
  const fromMeals = new Map<string, string>();
  meals.forEach((meal) => {
    const recipe = meal.recipe;
    if (!recipe?.title) return;
    const translated = recipe.translatedTitles?.[language]?.trim()
      || (displayLanguageOf(recipe) === language ? recipe.title : '');
    if (translated) fromMeals.set(meal.recipeId, translated);
  });
  for (const [id, entry] of Object.entries(map)) {
    const title = entry.translatedTitles?.[language]?.trim() || fromMeals.get(id);
    if (title && title !== entry.title) map[id] = { ...entry, title };
  }
  return map;
}
